// Recursive-descent parser for firestore.rules. Paths (`/users/$(uid)`) are scanned at
// character level where an expression or match path starts with "/".
import type { Allow, Expr, Fn, Match, RulesFile, Seg } from "./ast.ts";

export class RulesSyntaxError extends Error {
  readonly line: number;
  readonly col: number;
  constructor(message: string, line: number, col: number) {
    super(`line ${line}:${col}: ${message}`);
    this.line = line;
    this.col = col;
  }
}

const METHODS = new Set(["read", "write", "get", "list", "create", "update", "delete"]);
const SEG_CHARS = /[A-Za-z0-9_\-.~%@!+*]/;

export function parseRules(src: string): RulesFile {
  let pos = 0;

  const lineCol = (p = pos) => {
    const before = src.slice(0, p).split("\n");
    return [before.length, before[before.length - 1]!.length + 1] as const;
  };
  const fail = (msg: string, p = pos): never => {
    const [l, c] = lineCol(p);
    throw new RulesSyntaxError(msg, l, c);
  };
  const ws = () => {
    for (;;) {
      const m = /^(?:\s+|\/\/[^\n]*|\/\*[\s\S]*?\*\/)/.exec(src.slice(pos));
      if (!m) return;
      pos += m[0].length;
    }
  };
  const peek = (s: string) => {
    ws();
    return src.startsWith(s, pos);
  };
  const peekWord = (): string | null => {
    ws();
    const m = /^[A-Za-z_][A-Za-z0-9_]*/.exec(src.slice(pos));
    return m ? m[0] : null;
  };
  const eat = (s: string) => {
    if (!peek(s)) fail(`expected "${s}"`);
    pos += s.length;
  };
  const tryEat = (s: string) => {
    if (!peek(s)) return false;
    // Don't split operators: "=" vs "==", "<" vs "<=", "&" vs "&&" …
    const next = src[pos + s.length];
    if ((s === "=" || s === "<" || s === ">" || s === "!") && next === "=") return false;
    if (/^[A-Za-z_]/.test(s) && next && /[A-Za-z0-9_]/.test(next)) return false;
    pos += s.length;
    return true;
  };
  const word = (): string => peekWord() ?? fail("expected a name");
  const eatWord = () => {
    const w = word();
    pos += w.length;
    return w;
  };
  const line = () => lineCol()[0];

  // ---- expressions --------------------------------------------------------------------
  const expr = (): Expr => ternary();
  const ternary = (): Expr => {
    const c = or();
    if (tryEat("?")) {
      const t = expr();
      eat(":");
      return { k: "cond", c, t, f: expr() };
    }
    return c;
  };
  const or = (): Expr => {
    let l = and();
    while (tryEat("||")) l = { k: "bin", op: "||", l, r: and() };
    return l;
  };
  const and = (): Expr => {
    let l = rel();
    while (tryEat("&&")) l = { k: "bin", op: "&&", l, r: rel() };
    return l;
  };
  const rel = (): Expr => {
    let l = add();
    for (;;) {
      ws();
      const op = ["==", "!=", "<=", ">=", "<", ">"].find((o) => src.startsWith(o, pos));
      if (op) {
        pos += op.length;
        l = { k: "bin", op, l, r: add() };
      } else if (peekWord() === "in") {
        pos += 2;
        l = { k: "bin", op: "in", l, r: add() };
      } else if (peekWord() === "is") {
        pos += 2;
        l = { k: "is", e: l, type: eatWord() };
      } else return l;
    }
  };
  const add = (): Expr => {
    let l = mul();
    for (;;) {
      ws();
      const c = src[pos];
      if (c === "+" || (c === "-" && src[pos + 1] !== "-")) {
        pos++;
        l = { k: "bin", op: c, l, r: mul() };
      } else return l;
    }
  };
  const mul = (): Expr => {
    let l = unary();
    for (;;) {
      ws();
      const c = src[pos];
      if (c === "*" || c === "%" || (c === "/" && src[pos + 1] !== "/" && src[pos + 1] !== "*")) {
        pos++;
        l = { k: "bin", op: c, l, r: unary() };
      } else return l;
    }
  };
  const unary = (): Expr => {
    if (tryEat("!")) return { k: "un", op: "!", e: unary() };
    if (peek("-") && !/\d/.test(src[pos + 1] ?? "")) {
      pos++;
      return { k: "un", op: "-", e: unary() };
    }
    return postfix(primary());
  };
  const postfix = (e: Expr): Expr => {
    for (;;) {
      if (tryEat(".")) e = { k: "member", obj: e, name: eatWord() };
      else if (tryEat("(")) {
        const args: Expr[] = [];
        if (!tryEat(")")) {
          do args.push(expr());
          while (tryEat(","));
          eat(")");
        }
        e = { k: "call", fn: e, args };
      } else if (tryEat("[")) {
        if (tryEat(":")) {
          const to = peek("]") ? null : expr();
          eat("]");
          e = { k: "slice", obj: e, from: null, to };
        } else {
          const idx = expr();
          if (tryEat(":")) {
            const to = peek("]") ? null : expr();
            eat("]");
            e = { k: "slice", obj: e, from: idx, to };
          } else {
            eat("]");
            e = { k: "index", obj: e, idx };
          }
        }
      } else return e;
    }
  };
  const str = (): string => {
    const raw = src[pos] === "r";
    if (raw) pos++;
    const q = src[pos]!;
    pos++;
    let out = "";
    while (pos < src.length && src[pos] !== q) {
      let c = src[pos++]!;
      if (c === "\\" && !raw) {
        const e = src[pos++]!;
        if (e === "u") {
          c = String.fromCharCode(parseInt(src.slice(pos, pos + 4), 16));
          pos += 4;
        } else c = ({ n: "\n", t: "\t", r: "\r", "0": "\0" } as Record<string, string>)[e] ?? e;
      }
      out += c;
    }
    if (src[pos] !== q) fail("unterminated string");
    pos++;
    return out;
  };
  const pathLit = (): Expr => {
    const parts: (string | Expr)[] = [];
    while (src[pos] === "/") {
      pos++;
      if (src.startsWith("$(", pos)) {
        pos += 2;
        parts.push(expr());
        eat(")");
      } else if (src[pos] === "(") {
        pos++;
        parts.push(expr());
        eat(")");
      } else {
        const start = pos;
        while (pos < src.length && SEG_CHARS.test(src[pos]!) && src[pos] !== ",") pos++;
        if (pos === start) fail("empty path segment");
        parts.push(src.slice(start, pos));
      }
    }
    return { k: "path", parts };
  };
  const primary = (): Expr => {
    ws();
    const c = src[pos];
    if (c === "(") {
      pos++;
      const e = expr();
      eat(")");
      return e;
    }
    if (c === "[") {
      pos++;
      const items: Expr[] = [];
      if (!tryEat("]")) {
        do {
          if (peek("]")) break;
          items.push(expr());
        } while (tryEat(","));
        eat("]");
      }
      return { k: "list", items };
    }
    if (c === "{") {
      pos++;
      const entries: [string, Expr][] = [];
      if (!tryEat("}")) {
        do {
          if (peek("}")) break;
          ws();
          const key = src[pos] === "'" || src[pos] === '"' ? str() : eatWord();
          eat(":");
          entries.push([key, expr()]);
        } while (tryEat(","));
        eat("}");
      }
      return { k: "map", entries };
    }
    if (c === "'" || c === '"' || (c === "r" && (src[pos + 1] === "'" || src[pos + 1] === '"'))) return { k: "lit", v: str() };
    if (c === "/") return pathLit();
    const num = /^-?(?:\d+\.\d*|\.\d+|\d+)(?:[eE][+-]?\d+)?/.exec(src.slice(pos));
    if (num) {
      pos += num[0].length;
      return { k: "lit", v: Number(num[0]), float: /[.eE]/.test(num[0]) };
    }
    const w = peekWord();
    if (!w) return fail("expected an expression");
    pos += w.length;
    if (w === "true" || w === "false") return { k: "lit", v: w === "true" };
    if (w === "null") return { k: "lit", v: null };
    return { k: "id", name: w };
  };

  // ---- declarations --------------------------------------------------------------------
  const matchPath = (): Seg[] => {
    ws();
    const segs: Seg[] = [];
    while (src[pos] === "/") {
      pos++;
      if (src[pos] === "{") {
        const m = /^\{([A-Za-z_][A-Za-z0-9_]*)(=\*\*)?\}/.exec(src.slice(pos)) ?? fail("bad wildcard");
        pos += m[0].length;
        segs.push({ name: m[1]!, rest: !!m[2] });
      } else {
        const start = pos;
        while (pos < src.length && /[A-Za-z0-9_\-.~%]/.test(src[pos]!)) pos++;
        if (pos === start) fail("empty match segment");
        segs.push({ lit: src.slice(start, pos) });
      }
    }
    if (!segs.length) fail("match needs a path");
    return segs;
  };
  const fn = (): Fn => {
    const l = line();
    const name = eatWord();
    eat("(");
    const params: string[] = [];
    if (!tryEat(")")) {
      do params.push(eatWord());
      while (tryEat(","));
      eat(")");
    }
    eat("{");
    const lets: [string, Expr][] = [];
    while (peekWord() === "let") {
      pos += 3;
      const n = eatWord();
      eat("=");
      lets.push([n, expr()]);
      tryEat(";");
    }
    if (peekWord() !== "return") fail('expected "return"');
    pos += 6;
    const body = expr();
    tryEat(";");
    eat("}");
    return { name, params, lets, body, line: l };
  };
  const allow = (): Allow => {
    const l = line();
    const methods: string[] = [];
    do {
      const m = eatWord();
      if (!METHODS.has(m)) fail(`unknown method "${m}"`);
      methods.push(m);
    } while (tryEat(","));
    let cond: Expr | null = null;
    if (tryEat(":")) {
      if (peekWord() !== "if") fail('expected "if"');
      pos += 2;
      cond = expr();
    }
    tryEat(";");
    return { methods, cond, line: l };
  };
  const block = (m: Match) => {
    eat("{");
    while (!tryEat("}")) {
      if (pos >= src.length) fail("unexpected end of file");
      const w = eatWord();
      if (w === "match") m.children.push(matchDecl());
      else if (w === "function") m.fns.push(fn());
      else if (w === "allow") m.allows.push(allow());
      else fail(`unexpected "${w}"`);
    }
  };
  const matchDecl = (): Match => {
    const l = line();
    const m: Match = { segs: matchPath(), fns: [], allows: [], children: [], line: l };
    block(m);
    return m;
  };

  let version: string | null = null;
  if (peekWord() === "rules_version") {
    pos += "rules_version".length;
    eat("=");
    ws();
    version = str();
    tryEat(";");
  }
  if (peekWord() !== "service") fail('expected "service"');
  pos += 7;
  ws();
  const svc = /^[A-Za-z0-9_.]+/.exec(src.slice(pos)) ?? fail("expected a service name");
  pos += svc[0].length;
  const root: Match = { segs: [], fns: [], allows: [], children: [], line: 1 };
  block(root);
  ws();
  if (pos < src.length) fail("text after the service block");
  return { version, service: svc[0], fns: root.fns, matches: root.children };
}
